import moment from "moment";
/**
 * Generates a filtered list of valid days based on various configurations, the new selected day,
 * and the cutoff date for the selected day.
 *
 * If there is no dayFilterConfig the module is not on the flow so all values returned are undefined.
 *
 * @param {Cart["daysOfWeek"]} daysOfWeek - Array of days of the week (0-6) to filter by.
 * @param {Model["Recipes"]} recipes - Array of recipes.
 * @param {Model["DeliveryDays"]} deliveryDays - Array of delivery days containing meal dates and ship dates.
 * @param {Cart["startDate"]} startDate - The start date of the order.
 * @param {Cart["isSubscription"]} isSubscription - Indicates if the order is a subscription.
 * @param {Cart["subscriptionPeriod"]} subscriptionPeriod - Number of weeks in the subscription period.
 * @param {Cart["oneTimeOrderPeriod"]} oneTimeOrderPeriod - Number of weeks for one-time orders.
 * @param {Model["StartSunday"]} startSunday - Indicates whether the week starts on Sunday.
 * @param {DayFilterModuleConfig | undefined} dayFilterConfig - Module configuration.
 * @param {boolean} onlyShowDeliveryDays - If true, only delivery days are included in the results.
 * @param {GetDayFilterDaysFunctionType} getDayFilterDays - Function to filter and retrieve valid days.
 * @param {GetCutoffDateFunctionType} getCutoffDateForMealDate - Function to determine the cutoff date for a given meal date.
 * @returns {{
 *   dayFilterDays: Moment[],
 *   selectedDay: Moment | undefined,
 *   cutoffDay: Moment | undefined
 * }} An object containing:
 * - `dayFilterDays`: A sorted array of days.
 * - `selectedDay`: The first valid day in the filtered list, or `undefined` if no days are available.
 * - `cutoffDay`: The calculated cutoff date for the selected day, or `undefined` if no selected day exists.
 */
export const getNewDayFilterDays = (daysOfWeek, recipes, deliveryDays, startDate, isSubscription, subscriptionPeriod, oneTimeOrderPeriod, dayFilterConfig, onlyShowDeliveryDays, getDayFilterDays, getCutoffDateForMealDate) => {
    // If there is no day filter module on the flow, the selectedDay is the start date
    if (!dayFilterConfig) {
        return {
            cutoffDay: getCutoffDateForMealDate(moment(startDate), deliveryDays),
            dayFilterDays: [],
            selectedDay: moment(startDate),
        };
    }
    const { OnlyDaysWithDefaultMeals, OnlySelectedDaysOfWeek, DayCount } = dayFilterConfig;
    const dayFilterDays = getDayFilterDays(daysOfWeek, recipes, deliveryDays, startDate, isSubscription, subscriptionPeriod, oneTimeOrderPeriod, OnlySelectedDaysOfWeek, OnlyDaysWithDefaultMeals, onlyShowDeliveryDays, DayCount);
    const selectedDay = dayFilterDays && dayFilterDays.length > 0 ? dayFilterDays[0] : undefined;
    const cutoffDay = selectedDay ? getCutoffDateForMealDate(selectedDay, deliveryDays) : undefined;
    return {
        dayFilterDays,
        selectedDay,
        cutoffDay,
    };
};
/**
 * This function combines `newMeals` and `existingMeals`. It ensures:
 * - Meals from `existingMeals` are retained and take precedence when possible, preserving their quantities and assignments.
 * - Meals for dates not present in `existingMeals` are added from `newMeals`.
 * - Any meals in `existingMeals` that are not part of `newMeals` (e.g., meals separate from the template) are included in the final list.
 *
 * @param {Meal[]} newMeals - An array of new meals generated from the template.
 * @param {Meal[]} existingMeals - An array of existing meals.
 * @param {moment.Moment[]} mealDatesForOrderPeriod - A list of dates for the order period.
 *
 * @returns {Meal[]} - A reconciled array of meals, combining `newMeals` and `existingMeals`, updated to reflect changes and preserve manual additions.
 *
 */
export const mergeNewAndExistingMeals = (newMeals, existingMeals, mealDatesForOrderPeriod) => {
    // Step 1: Filter existingMeals to include only those matching the specified meal dates.
    const existingMealsForMealDates = existingMeals.filter((existingMeal) => mealDatesForOrderPeriod.some((date) => moment(existingMeal.MealDate).isSame(date, "day")));
    // Step 2: Group the filtered existingMeals by their MealDate.
    const existingMealsByDate = existingMealsForMealDates.reduce((acc, existingMeal) => {
        const date = moment(existingMeal.MealDate).format("YYYY-MM-DD");
        acc[date] = acc[date] || [];
        acc[date].push(existingMeal);
        return acc;
    }, {});
    // Step 3: Map over the newMeals to:
    // - Replace meals with their matching existingMeal (to maintain quantity and assignments).
    // - Keep unmatched, new meals unchanged.
    const updatedMeals = newMeals.map((meal) => {
        const date = moment(meal.MealDate).format("YYYY-MM-DD");
        const existingMealsForDate = existingMealsByDate[date] || [];
        // Find a matching meal for the same date and ProgramMealId
        const matchingMeal = existingMealsForDate.find((existingMeal) => existingMeal.ProgramMealId === meal.ProgramMealId && moment(meal.MealDate).isSame(existingMeal.MealDate, "day"));
        // Use the existing meal to maintain quantity and assignments
        if (matchingMeal) {
            return matchingMeal;
        }
        // Otherwise, return the meal unchanged
        return meal;
    });
    const mergedMeals = [...updatedMeals];
    // Step 4: Add any meals from existingMeals that are not already in newMeals.
    // - This occurs when a meal is added that is not part of the template so it will not be in the newMeals array.
    Object.entries(existingMealsByDate).forEach(([date, mealsByProgramId]) => {
        const newMealsForExistingDate = newMeals.filter((nm) => moment(nm.MealDate).isSame(date, "day"));
        if (newMealsForExistingDate.length === 0) {
            mealsByProgramId.forEach((existingMeal) => {
                const alreadyInMerged = mergedMeals.some((m) => moment(m.MealDate).isSame(existingMeal.MealDate, "day") && m.ProgramMealId === existingMeal.ProgramMealId);
                if (!alreadyInMerged) {
                    mergedMeals.push(existingMeal);
                }
            });
        }
    });
    return mergedMeals;
};
